import { AmplifyBaseQueryError } from '../../../api/apiBaseQuery';
import { ConnectionsProfile } from '../../../api/expert/types';
import { rootApi } from '../../../app/rootApi';
import {
    ExpertInsurer,
    FullProfile,
    GetInsurersResponse,
    GetProfilePhotoResponse,
    PutProfilePhotoRequest,
    PutProfilePhotoResponse,
    PutProfileRequest,
    ProfileTopic,
} from './types';

const cacheTtlSeconds = 60 * 60;
const cacheTtlSecondsProfilePhoto = 60 * 60 * 24;

const profileApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getAllProfiles: builder.query<ConnectionsProfile[], null>({
            queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
                try {
                    let profiles: ConnectionsProfile[] = [];
                    const batchSize = 50;
                    let page = 0;
                    let reachedEnd = false;

                    let error: AmplifyBaseQueryError | undefined = undefined;

                    while (reachedEnd === false) {
                        const result = await fetchWithBQ({
                            api: 'expert',
                            endpoint: `profiles/list?batchSize=${batchSize}&page=${page}`,
                            method: 'GET',
                        });

                        if (result.error) {
                            error = result.error;
                            break;
                        }

                        if (result.data) {
                            const data = result.data as ConnectionsProfile[];
                            profiles.push(...data);
                            if (data.length < batchSize) {
                                reachedEnd = true;
                            } else {
                                page++;
                            }
                        } else {
                            reachedEnd = true;
                        }
                    }

                    profiles = profiles.filter(
                        (profile) => !profile.firstName.includes('?') || !profile.lastName.includes('?')
                    );

                    if (error) {
                        return { error };
                    } else {
                        return { data: profiles };
                    }
                } catch (exception) {
                    return { error: exception as AmplifyBaseQueryError };
                }
            },
            providesTags: ['Profiles'],
            keepUnusedDataFor: cacheTtlSeconds,
        }),
        getFullProfileByExpertId: builder.query<FullProfile, string>({
            query: (expertId) => ({
                api: 'expert',
                endpoint: `profiles/${expertId}/full`,
                method: 'GET',
            }),
            providesTags: ['Profile'],
            keepUnusedDataFor: cacheTtlSeconds,
        }),
        putProfile: builder.mutation<object, PutProfileRequest>({
            query: ({ patch }) => ({
                api: 'expert',
                endpoint: `profiles`,
                method: 'PUT',
                data: patch,
            }),
            invalidatesTags: ['Profile'],
        }),
        putProfilePhoto: builder.mutation<PutProfilePhotoResponse, PutProfilePhotoRequest>({
            query: ({ mimeType }) => ({
                api: 'expert',
                endpoint: `profiles/signedPhotoUrl`,
                method: 'PUT',
                data: {
                    mimeType: mimeType,
                },
            }),
            transformResponse: (response: { data: PutProfilePhotoResponse }) => {
                return response.data;
            },
            invalidatesTags: ['ProfilePhoto'],
        }),
        getProfilePhoto: builder.query<string, string>({
            query: (expertId) => ({
                api: 'expert',
                endpoint: `profiles/${expertId}/signedPhotoUrl`,
                method: 'GET',
            }),
            transformResponse: (response: GetProfilePhotoResponse) => {
                return response.presignedGetUrl;
            },
            transformErrorResponse: () => {
                return null;
            },
            providesTags: ['ProfilePhoto'],
            keepUnusedDataFor: cacheTtlSecondsProfilePhoto,
        }),
        getInsurersForExpert: builder.query<ExpertInsurer[], string>({
            query: (expertId) => ({
                api: 'expert',
                endpoint: `profiles/${expertId}/insurers`,
                method: 'GET',
            }),
            transformResponse: (response) => (response as GetInsurersResponse).insurers,
            providesTags: ['ProfileInsurers'],
            keepUnusedDataFor: cacheTtlSeconds,
        }),
        getExpertCanHelpWith: builder.query<ProfileTopic[], void>({
            query: () => ({
                api: 'expert',
                endpoint: `profiles/expertCanHelpWith`,
                method: 'GET',
            }),
            providesTags: ['ProfileSpecialisms'],
            keepUnusedDataFor: cacheTtlSeconds,
        }),
        getExpertTreatmentApproaches: builder.query<ProfileTopic[], void>({
            query: () => ({
                api: 'expert',
                endpoint: `profiles/treatmentApproaches`,
                method: 'GET',
            }),
            providesTags: ['ProfileTreatmentApproaches'],
            keepUnusedDataFor: cacheTtlSeconds,
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetAllProfilesQuery,
    useLazyGetAllProfilesQuery,
    useGetFullProfileByExpertIdQuery,
    usePutProfileMutation,
    useGetProfilePhotoQuery,
    useLazyGetProfilePhotoQuery,
    usePutProfilePhotoMutation,
    useGetInsurersForExpertQuery,
    useGetExpertCanHelpWithQuery,
    useGetExpertTreatmentApproachesQuery,
} = profileApi;
